import { Component, OnInit, AfterViewInit, ElementRef, ViewChild, HostListener, OnDestroy, QueryList, ViewChildren, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material';

import { LoadingStateService } from 'viega-common/services/loading-state-service';

import { ModelDataService } from "../../../core/model-data.service";
import { ArticleDataService } from "../../../core/article-data.service";
import { WebTrekkService } from '../../../core/web-trekk.service';
import { PathService } from '../../../core/path.service';
import { IModel } from '../../../core/IModel';
import { IArticle } from '../../../core/IArticle';
import { IPlateColorpickerDialogData, IPlateColorpickerDialogResult, PlateColorpickerDialogComponent } from '../../../shared/plate-colorpicker-dialog/plate-colorpicker-dialog.component';
import { AppStateService } from 'app/core/app-state.service';

interface Point {
    x: number;
    y: number;
}

interface SlideIndexObj {
    index2d: Point;
    offset2d: Point;
}

@Component({
    selector: 'article-carousel',
    templateUrl: './article-carousel.component.html',
    styleUrls: ['./article-carousel.component.scss'],
})
export class ArticleCarouselComponent implements OnInit, AfterViewInit {
    private isViewInitialized: boolean = false;

    private dragStart: Point;
    private dragStartTime: number;
    private dragDelta: Point = { x: 0, y: 0 };
    private isHorizontalDrag: boolean;
    private slideIndexes: SlideIndexObj[];
    private slidingElements: number;
    /** Size of one slide element. */
    private slideSize: Point;
    /** The index of the current article per each plate. */
    private currentArticleIndexes: { [plateIndex: number]: number };
    /** Mode should also be appended to this */
    private uniqueIdPrefix: string = "._ma";
    private wasDragging: boolean = false;

    /** 'wc' or 'urinal' */
    @Input()
    public mode: string = 'wc';

    /** The index of the plate which is currently being displayed to the user. */
    public currentPlateIndex: number = 0;
    /** The index of the article which is currently being displayed to the user. */
    public currentArticleIndex: number = 0;
    public plates: IModel[];
    public plateArticles: IArticle[][];
    public plateSpecialArticles: IArticle[][];
    public transitionSpeed = 100; // ms
    public startsDragging: boolean = false;
    public isDragging: boolean = false;

    public get currentPlate(): IModel {
        return this.plates[this.currentPlateIndex];
    }

    public get currentArticle(): IArticle {
        return this.currentArticles[this.currentArticleIndex];
    }

    public get currentArticles(): IArticle[] {
        return this.plateArticles[this.currentPlateIndex];
    }

    @ViewChild("measureItem")
    public measureItem: ElementRef;

    @ViewChildren("plates")
    public platesNgFor: QueryList<any>;

    constructor(
        private modelService: ModelDataService,
        private pathService: PathService,
        private appState: AppStateService,
        public articleService: ArticleDataService,
        private loadingService: LoadingStateService,
        private hostElement: ElementRef,
        private router: Router,
        private route: ActivatedRoute,
        private tracker: WebTrekkService,
        private dialog: MatDialog) {}

    public ngOnInit() {
        this.plates = [];
        this.plateArticles = [];
        this.plateSpecialArticles = [];
        this.currentArticleIndexes = [];

        this.loadingService.addLoad();
        const subscription = this.modelService.getPlates(true) // TODO: set to false next time after running the aggregator
            .filter(m => {
                let appliance: number;
                if (this.mode === 'urinal')
                    appliance = 0;
                else if (this.mode === 'wc')
                    appliance = 1;
                else
                    throw new Error(`The configured mode ${this.mode} is invalid.`);

                return (m.attributes["plate-appliance"][0] === appliance);
            })
            .toArray()
            .subscribe(
                (plates: IModel[]) => plates.forEach(p => this.receivePlate(p)),
                console.error,
                () => this.loadingService.removeLoad());

        this.tracker.trackEvent('mobile');

        this.appState.setMobilePulldown(false);
    }

    private receivePlate(plate: IModel) {
        const plateIndex = this.plates.push(plate) - 1;

        this.loadingService.addLoad();
        const subscription = this.articleService.getArticlesOfModel(false, plate.key)
            .toArray()
            .subscribe(
                (articles: IArticle[]) => {
                    // make sure there's only one special color article in the list
                    let containsSpecialArticle: boolean = false;
                    for (let i = 0; i < articles.length; i++) {
                        const article = articles[i];
                        if (this.articleService.isSpecialColorArticle(article)) {
                            if (containsSpecialArticle) {
                                articles.splice(i, 1);
                                i--;
                            }

                            containsSpecialArticle = true;
                            if (this.plateSpecialArticles[plateIndex] === undefined)
                                this.plateSpecialArticles[plateIndex] = [];

                            this.plateSpecialArticles[plateIndex].push(article);
                        }
                    }

                    this.plateArticles[plateIndex] = articles;
                    this.currentArticleIndexes[plateIndex] = 0;
                },
                console.error,
                () => this.loadingService.removeLoad());
    }

    public ngAfterViewInit() {
        // some plates may have been rendered already. If not, we gotta wait for ngFor to render anything.
        if (this.platesNgFor.length > 0) {
            this.onLoadComplete();
        } else {
            const platesNgForSubscription = this.platesNgFor.changes.subscribe(n => {
                this.onLoadComplete();
                platesNgForSubscription.unsubscribe();
            },
            console.error);
        }

        this.currentPlateIndex = 0;
        this.currentArticleIndex = 0;
    }

    private onLoadComplete() {
        console.assert(this.plates.length > 0, "no plates were received");
        this.remeasure();
        this.resetSlides();
    }

    @HostListener('window:resize', ['$event'])
    public onResize(event) {
        this.remeasure();
        this.resetSlides();
    }

    @HostListener("mousedown", ['$event'])
    public onMouseDown(event: MouseEvent) {
        if (event.which !== 1)
            return;

        if (this.isDragging) {
            this.endDrag();
            return;
        }

        event.preventDefault();
        this.initDrag({
            x: event.pageX,
            y: event.pageY
        });

        event.stopPropagation();
    }

    @HostListener("mousemove", ['$event'])
    public onMouseMove(event: MouseEvent) {
        this.performDrag({
            x: event.pageX,
            y: event.pageY
        });

        event.preventDefault();
        event.stopPropagation();
    }

    @HostListener("mouseup", ['$event'])
    public onMouseUp(event: MouseEvent) {
        if (event.which !== 1)
            return;

        this.endDrag();

        if (!this.wasDragging)
            this.chooseArticle(this.currentArticle);

        event.preventDefault();
        event.stopPropagation();
    }

    @HostListener("mouseout", ['$event'])
    public onMouseOut(event: MouseEvent) {
        const outElement = event.relatedTarget as any;
        if (outElement === null || outElement.nodeName === 'HTML' || outElement.nodeName === 'BODY')
            this.endDrag();
    }

    // NOTE: do NOT use TouchEvent here. It will break firefox compatibility!
    @HostListener("touchstart", ['$event'])
    public onTouchStart(event: any) {
        const primaryTouch: Touch = event.touches[0];

        this.initDrag({
            x: primaryTouch.pageX,
            y: primaryTouch.pageY
        });

        event.stopPropagation();
    }

    // NOTE: do NOT use TouchEvent here. It will break firefox compatibility!
    @HostListener("touchmove", ['$event'])
    public onTouchMove(event: any) {
        const isMultitouch = event.touches.length > 1;
        if (isMultitouch)
            return;

        const touch: Touch = event.touches[0];
        this.performDrag({
            x: touch.pageX,
            y: touch.pageY
        });
    }

    // NOTE: do NOT use TouchEvent here. It will break firefox compatibility!
    @HostListener("touchend", ['$event'])
    public onTouchEnd(event: any) {
        this.endDrag();

        event.stopPropagation();

        if (!this.wasDragging)
            this.chooseArticle(this.currentArticle);
    }

    // NOTE: do NOT use TouchEvent here. It will break firefox compatibility!
    @HostListener("touchcancel", ['$event'])
    public onTouchCancel(event: any) {
        this.endDrag();
        event.stopPropagation();
    }

    private chooseArticle(article: IArticle) {
        // query parameters meant to be applied to any route called from here
        const baseQueryParams = this.appState.urlQueryParamsBaseMap();

        if (this.articleService.isSpecialColorArticle(article)) {
            const specialArticles: IArticle[] = this.plateSpecialArticles[this.currentPlateIndex];

            const dialogData: IPlateColorpickerDialogData = {
                specialColorArticles: specialArticles,
                isMobileMode: true
            };
            const dialogRef = this.dialog.open(PlateColorpickerDialogComponent, {
                data: dialogData
            });

            dialogRef.afterClosed().subscribe((result: IPlateColorpickerDialogResult) => {
                if (result === undefined)
                    return; // do nothing

                baseQueryParams.ac = result.selectedSpecialColor.stringRepresentation;
                this.router.navigate(['.', result.selectedArticle.key], { queryParams: baseQueryParams, relativeTo: this.route });
            },
            console.error);
        } else {
            this.router.navigate(['.', this.currentArticle.key], { queryParams: baseQueryParams, relativeTo: this.route });
        }
    }

    /** Instantly moves all slides to their initial position. */
    public resetSlides() {
        for (let x = 0; x < this.plates.length; x++) {
            for (let y = 0; y < this.plateArticles[x].length; y++) {
                this.setSlidePosition({ x, y }, 10000, 0, 0);
            }
        }

        this.setSlidePosition({ x: this.currentPlateIndex, y: this.currentArticleIndex }, 0, 0);
    }

    /** Measures the size of one slide / article. */
    public remeasure() {
        console.assert(this.measureItem !== undefined, "#measureItem doesn't seem to be in the view.");
        this.slideSize = {
            x: this.measureItem.nativeElement.clientWidth,
            y: this.measureItem.nativeElement.clientHeight
        };
    }

    private initDrag(pageLocation: Point) {
        this.dragStart = pageLocation;
        this.dragStartTime = Date.now(); // to determine touch duration
        this.startsDragging = true;
        this.isDragging = false;
        this.wasDragging = false;
    }

    private performDrag(currentLocation: Point) {
        if (!this.startsDragging)
            return;

        this.dragDelta = {
            x: currentLocation.x - this.dragStart.x,
            y: currentLocation.y - this.dragStart.y
        };

        // initialize some more variables for the current drag operation once the direction is known
        if (!this.isDragging) {
            this.isHorizontalDrag = (Math.abs(this.dragDelta.x) > Math.abs(this.dragDelta.y));
            this.setSlideIndexes();
            this.moveSlides(0, 0);
            this.isDragging = true;
            this.wasDragging = false;
        } else {
            // increase drag resistance when there's just one element
            if (this.slidingElements === 1)  {
                if (this.isHorizontalDrag)
                    this.dragDelta.x /= 6;
                else
                    this.dragDelta.y /= 6;
            }

            this.moveSlides(this.isHorizontalDrag ? this.dragDelta.x : this.dragDelta.y, 0);
        }

        // consider this an actual dragging operation only, if the element has been dragged over a certain distance.
        // this is important for distinguishing between the "click" of an article and a drag of it .
        if (!this.wasDragging && (Math.abs(this.dragDelta.x) > 10 || Math.abs(this.dragDelta.y) > 10)) {
            this.wasDragging = true;
        }
    }

    private endDrag() {
        if (!this.isDragging) {
            // ensure that an upcoming drag operation is fully cancelled, even if a click happened but the mouse hasn't been moved yet
            this.startsDragging = false;
            return;
        }

        const isShortDuration = Number(Date.now() - this.dragStartTime) < 250;
        if (this.isHorizontalDrag) {
            const isValidSlide =
                (isShortDuration && Math.abs(this.dragDelta.x) > 20) ||
                Math.abs(this.dragDelta.x) > this.slideSize.x / 5;

            if (isValidSlide && this.slidingElements > 1) {
                const direction = this.dragDelta.x > 0 ? 1 : -1;

                this.moveSlides(this.slideSize.x * direction, this.transitionSpeed);

                this.currentPlateIndex = this.offsetPlateIndex(-direction);
                this.currentArticleIndex = this.currentArticleIndexes[this.currentPlateIndex];
            } else {
                this.moveSlides(0, this.transitionSpeed);
            }
        } else {
            const isValidSlide =
                (isShortDuration && Math.abs(this.dragDelta.y) > 20) ||
                Math.abs(this.dragDelta.y) > this.slideSize.y / 5;

            if (isValidSlide && this.slidingElements > 1) {
                const direction = this.dragDelta.y > 0 ? 1 : -1;

                this.moveSlides(this.slideSize.y * direction, this.transitionSpeed);

                this.currentArticleIndex = this.currentArticleIndexes[this.currentPlateIndex] =
                    this.offsetArticleIndex(-direction);
            } else {
                this.moveSlides(0, this.transitionSpeed);
            }
        }

        this.startsDragging = false;
        this.isDragging = false;
    }

    /**
     * When a slide operation takes place, three items are involved: the current slide, the previous and the next
     * This method will find the correct slides with their indexes and offset indexes, so that the actual slide
     * operation has all necessary data prepared.
     */
    private setSlideIndexes() {
        if (this.isHorizontalDrag) {
            const prevPlateIdx = this.offsetPlateIndex(-1);
            const prevPlateArticleIdx = this.currentArticleIndexes[prevPlateIdx];
            const nextPlateIdx = this.offsetPlateIndex(+1);
            const nextPlateArticleIdx = this.currentArticleIndexes[nextPlateIdx];

            this.slideIndexes = [
                {
                    index2d: { x: prevPlateIdx, y: prevPlateArticleIdx },
                    offset2d: { x: -1, y: 0 }
                },
                {
                    index2d: { x: this.currentPlateIndex, y: this.currentArticleIndex },
                    offset2d: { x: 0, y: 0 }
                },
                {
                    index2d: { x: nextPlateIdx, y: nextPlateArticleIdx },
                    offset2d: { x: +1, y: 0 }
                }];

            if (this.slideIndexes[0].index2d.x === this.slideIndexes[2].index2d.x)
                this.slidingElements = 2;
            else
                this.slidingElements = 3;
        } else {
            if (this.currentArticles.length > 1) {
                this.slideIndexes = [
                    {
                        index2d: { x: this.currentPlateIndex, y: this.offsetArticleIndex(+1) },
                        offset2d: { x: 0, y: +1 }
                    },
                    {
                        index2d: { x: this.currentPlateIndex, y: this.currentArticleIndex },
                        offset2d: { x: 0, y: 0 }
                    },
                    {
                        index2d: { x: this.currentPlateIndex, y: this.offsetArticleIndex(-1) },
                        offset2d: { x: 0, y: -1 }
                    }];

                if (this.slideIndexes[0].index2d.y === this.slideIndexes[2].index2d.y)
                    this.slidingElements = 2;
                else
                    this.slidingElements = 3;
            } else {
                this.slideIndexes = [
                    {
                        index2d: { x: this.currentPlateIndex, y: this.currentArticleIndex },
                        offset2d: { x: 0, y: 0 }
                    }];
                this.slidingElements = 1;
            }
        }
    }

    private moveSlides(offset: number, transitionTime: number = 0) {
        this.slideIndexes.forEach((slideIndexObj: SlideIndexObj, index: number) => {
            let newPosX = 0;
            let newPosY = 0;
            if (this.isHorizontalDrag)
                newPosX = offset + (slideIndexObj.offset2d.x * this.slideSize.x);
            else
                newPosY = offset + (slideIndexObj.offset2d.y * this.slideSize.y);

            // if there are more than two items, move all three slides normally.
            // however, if there are only two items ensure that the second one isn't moved twice
            if (this.slidingElements !== 2 || index === 1 || (index === 0 && offset < 0) || (index === 2 && offset > 0))
                this.setSlidePosition(slideIndexObj.index2d, newPosX, newPosY, transitionTime);
        });
    }

    private setSlidePosition(index2d: Point, newPosX: number, newPosY: number, transitionTime: number = 0) {
        const element = this.getSlideElement(index2d);
        const elementRect = element.getBoundingClientRect();
        const isHorizontalMovement = newPosX !== 0;
        const isLongDistance =
            (isHorizontalMovement && Math.abs(elementRect.left - newPosX) > window.innerWidth) ||
            (!isHorizontalMovement && Math.abs(elementRect.top - newPosY) > window.innerHeight * 1.3);

        if (isLongDistance)
            transitionTime = 0;

        element.style.transition = `${transitionTime}ms`;
        element.style.transform = `translate(${newPosX}px, ${newPosY}px)`;
    }

    private getSlideElement(index2d: Point): HTMLElement {
        const name = `${this.uniqueIdPrefix}${this.mode}${index2d.x},${index2d.y}`;
        const element = document.getElementById(name);

        console.assert(element !== undefined);
        return element;
    }

    private offsetPlateIndex(offset: number): number {
        return ArticleCarouselComponent.circleIndex(this.currentPlateIndex + offset, this.plates.length);
    }

    private offsetArticleIndex(offset: number) {
        return ArticleCarouselComponent.circleIndex(this.currentArticleIndex + offset, this.currentArticles.length);
    }

    private static circleIndex(index: number, count: number): number {
        if (index < 0)
            return count + index;
        else if (index >= count)
            return index - count;
        else
            return index;
    }
}



// WEBPACK FOOTER //
// ./src/app/mobile/articles/article-carousel/article-carousel.component.ts